import type { ChuoiChiSo, DiemChuoiChiSo } from '@qlk/shared';
import { ngayDayVn, ngayGonVn } from '@qlk/shared';

import { Skeleton } from '../../../../ui/index.js';
import { dinhDangChiSo } from './dinhDangChiSo.js';

/**
 * Biểu đồ đường của **một** chỉ số: kỳ này nét liền có tô vùng, kỳ trước nét
 * đứt xám.
 *
 * Vẽ bằng SVG thuần (quyết định chốt số 5 — không thêm thư viện biểu đồ). Hệ
 * toạ độ để riêng ở đây chứ không dùng chung với `hieuSuat/BieuDoKy`: trục của
 * biểu đồ này **bắt đầu từ 0** và trần là `max × 1,1`, còn `BieuDoKy` lấy trần
 * đúng bằng `max` nên đường luôn chạm mép trên.
 *
 * Vì sao là một thành phần riêng, không nới `BieuDoKy`: chuỗi ở đây có điểm
 * `null` (ngày chưa có số liệu là **khoảng trống**, không phải 0) và có chuỗi
 * thứ hai, còn `BieuDoKy` đọc `TheChiSo` của bốn tab trang kênh — hợp đồng đã
 * chốt với `giaTri` không nullable.
 *
 * Hai chuỗi ghép theo **chỉ số thứ tự**, không theo khoá ngày: 01/10 nằm trên
 * 01/09. Kỳ trước ngắn hơn (tháng 2 so với tháng 3) thì nét đứt dừng sớm, chứ
 * không bị kéo giãn cho bằng.
 */

const RONG = 1000;
/** Chiều cao vùng vẽ, **không** tính dòng nhãn trục ngày. */
const CAO = 240;
const LE_PHAI = 64;
const LE_DUOI = 24;
/**
 * Lề trên của vùng vẽ.
 *
 * Nhỏ vì trần trục đã là `max × 1,1`: đỉnh đường tự có 10 % chỗ trống phía
 * trên, thêm lề nữa là hai lần cùng một khoảng trắng — đúng "khoảng trống thừa"
 * phải bỏ.
 */
const LE_TREN = 4;

/** Trần trục Y: 10 % trên giá trị lớn nhất, để đỉnh không chạm mép khung. */
export const HE_SO_TRAN = 1.1;

export interface BieuDoChiSoProps {
  du: ChuoiChiSo | undefined;
  nhan: string;
  dangTai: boolean;
  /** Tên kỳ cho chú thích (`28 ngày qua`). */
  nhanKy: string;
}

/** Toạ độ x của điểm thứ `i` trong `n` điểm. */
function toaDoX(i: number, n: number): number {
  if (n <= 1) return 0;
  return (i / (n - 1)) * (RONG - LE_PHAI);
}

/**
 * Toạ độ y của `giaTri` trên trục **bắt đầu từ 0**.
 *
 * Trục không bắt đầu từ 0 là cách làm một mức tăng 2 % trông như gấp đôi; mọi
 * biểu đồ số đếm của app đều phải neo ở 0.
 */
function toaDoY(giaTri: number, tran: number): number {
  if (tran <= 0) return CAO - LE_DUOI;
  const ty = Math.min(Math.max(giaTri / tran, 0), 1);
  return LE_TREN + (1 - ty) * (CAO - LE_DUOI - LE_TREN);
}

/**
 * Đường SVG của một chuỗi có lỗ.
 *
 * Mỗi đoạn liên tục là một lệnh `M`; gặp `null` thì ngắt bút. Nối thẳng qua lỗ
 * là vẽ một đoạn số liệu không tồn tại, mà trông y như số liệu thật.
 */
export function duongCoLo(diem: DiemChuoiChiSo[], soDiemTruc: number, tran: number): string {
  const phan: string[] = [];
  let dangVe = false;

  diem.forEach((d, i) => {
    if (d.giaTri === null) {
      dangVe = false;
      return;
    }
    const x = toaDoX(i, soDiemTruc).toFixed(1);
    const y = toaDoY(d.giaTri, tran).toFixed(1);
    phan.push(`${dangVe ? 'L' : 'M'} ${x} ${y}`);
    dangVe = true;
  });

  return phan.join(' ');
}

/**
 * Vùng tô dưới đường kỳ này — **một hình kín cho mỗi đoạn liên tục**.
 *
 * Không tô một hình duy nhất từ điểm đầu tới điểm cuối: khoảng trống giữa hai
 * đoạn sẽ bị tô lấp, và lúc đó "chưa có số liệu" trông y như "có số liệu".
 */
export function vungCoLo(diem: DiemChuoiChiSo[], soDiemTruc: number, tran: number): string {
  const day = CAO - LE_DUOI;
  const hinh: string[] = [];
  let dau: number | null = null;

  const dong = (cuoi: number) => {
    if (dau === null) return;
    /* Một điểm lẻ không có bề ngang nên không có vùng nào để tô. */
    if (cuoi > dau) {
      const d = diem
        .slice(dau, cuoi + 1)
        .map((x, k) => {
          const i = (dau as number) + k;
          return `${k === 0 ? 'M' : 'L'} ${toaDoX(i, soDiemTruc).toFixed(1)} ${toaDoY(x.giaTri ?? 0, tran).toFixed(1)}`;
        })
        .join(' ');
      hinh.push(
        `${d} L ${toaDoX(cuoi, soDiemTruc).toFixed(1)} ${String(day)}` +
          ` L ${toaDoX(dau, soDiemTruc).toFixed(1)} ${String(day)} Z`,
      );
    }
    dau = null;
  };

  diem.forEach((d, i) => {
    if (d.giaTri === null) {
      dong(i - 1);
      return;
    }
    dau ??= i;
  });
  dong(diem.length - 1);

  return hinh.join(' ');
}

export function BieuDoChiSo({ du, nhan, dangTai, nhanKy }: BieuDoChiSoProps) {
  if (dangTai) return <Skeleton className="h-[318px] w-full rounded-b-the" />;

  const diem = du?.diem ?? [];
  const diemTruoc = du?.diemTruoc ?? [];

  if (diem.length === 0) {
    return (
      <div className="rounded-b-the border border-t-0 border-vien bg-be-mat p-6 text-co-ban text-chu-mo">
        Chưa có số liệu trong khoảng này.
      </div>
    );
  }

  const dinhDang = du?.dinhDang ?? 'so';
  const currency = du?.currency ?? 'USD';

  /*
   * Trần của trục lấy trên **cả hai** chuỗi: lấy riêng kỳ này thì nét đứt của
   * một kỳ trước cao hơn sẽ chạy ra ngoài khung.
   */
  const soDiemTruc = diem.length;
  const moiGiaTri = [...diem, ...diemTruoc]
    .map((d) => d.giaTri)
    .filter((v): v is number => v !== null);
  const tran = Math.max(...moiGiaTri, 1) * HE_SO_TRAN;

  const duongNay = duongCoLo(diem, soDiemTruc, tran);
  const vungNay = vungCoLo(diem, soDiemTruc, tran);
  const duongTruoc = duongCoLo(diemTruoc, soDiemTruc, tran);
  const coKyTruoc = duongTruoc !== '';

  /* Bốn vạch lưới kể cả vạch 0 ở chân trục. */
  const vach = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="rounded-b-the border border-t-0 border-vien bg-be-mat p-4">
      {/*
        Dòng tiêu đề: tên chỉ số bên trái, chú thích hai nét ở **góc phải**.
        Chú thích nằm dưới biểu đồ thì mắt phải đi xuống rồi quay lên mới biết
        nét nào là kỳ nào.
      */}
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-tieu-de-khoi font-semibold text-chu">{nhan} · theo ngày</h3>

        <ul className="flex flex-wrap items-center gap-4 text-bang text-chu-phu">
          <li className="flex items-center gap-2">
            <span aria-hidden="true" className="inline-block h-0.5 w-6 rounded-full bg-nhan" />
            {nhanKy === '' ? 'Kỳ này' : nhanKy}
          </li>
          {coKyTruoc && (
            <li className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-0 w-6 border-t-2 border-dashed border-chu-mo"
              />
              Kỳ trước
            </li>
          )}
        </ul>
      </div>

      <svg
        viewBox={`0 0 ${String(RONG)} ${String(CAO + 16)}`}
        className="h-[240px] w-full"
        role="img"
        aria-label={`Biểu đồ ${nhan} theo ngày, kỳ này và kỳ trước`}
      >
        {vach.map((v) => {
          const y = toaDoY(tran * v, tran);
          return (
            <g key={v}>
              <line
                x1={0}
                x2={RONG - LE_PHAI}
                y1={y}
                y2={y}
                stroke="var(--color-vien)"
                strokeWidth={1}
              />
              <text
                x={RONG - LE_PHAI + 8}
                y={y + 4}
                className="so-lieu"
                fontSize={13}
                fill="var(--color-chu-phu)"
              >
                {dinhDangChiSo(tran * v, dinhDang, currency)}
              </text>
            </g>
          );
        })}

        {/* Cột xám cho khoảng YouTube chưa chốt số liệu */}
        {diem.map((d, i) =>
          d.daChot ? null : (
            <rect
              key={`chua-chot-${d.date}`}
              x={toaDoX(i, soDiemTruc) - 3}
              y={LE_TREN}
              width={6}
              height={CAO - LE_DUOI - LE_TREN}
              fill="var(--color-neutral-nen)"
            />
          ),
        )}

        {/* Kỳ trước **vẽ trước** để nét liền của kỳ này nằm trên */}
        {coKyTruoc && (
          <path
            d={duongTruoc}
            fill="none"
            stroke="var(--color-chu-mo)"
            strokeWidth={2}
            strokeDasharray="6 5"
          />
        )}

        {/* Vùng tô 8 %: đủ để mắt thấy khối lượng, nhẹ để nét đứt phía dưới
            vẫn đọc được. */}
        <path d={vungNay} fill="var(--color-nhan)" fillOpacity={0.08} />
        <path d={duongNay} fill="none" stroke="var(--color-nhan)" strokeWidth={2.5} />

        {/* Nhãn trục ngày: đầu, giữa, cuối */}
        {[...new Set([0, Math.floor(soDiemTruc / 2), soDiemTruc - 1])].map((i) => {
          const d = diem[i];
          if (!d) return null;
          return (
            <text
              key={`truc-${d.date}`}
              x={Math.min(Math.max(toaDoX(i, soDiemTruc), 24), RONG - LE_PHAI - 24)}
              y={CAO + 12}
              fontSize={13}
              fill="var(--color-chu-phu)"
              textAnchor="middle"
            >
              {ngayGonVn(d.date)}
            </text>
          );
        })}
      </svg>

      {/* Bảng chữ cho trình đọc màn hình: SVG không đọc được số liệu. */}
      <div className="sr-only">
        <table>
          <caption>{`${nhan} theo ngày`}</caption>
          <thead>
            <tr>
              <th scope="col">Ngày</th>
              <th scope="col">{nhan}</th>
            </tr>
          </thead>
          <tbody>
            {diem.map((d) => (
              <tr key={d.date}>
                <th scope="row">{ngayDayVn(d.date)}</th>
                <td>{dinhDangChiSo(d.giaTri, dinhDang, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
